import React, { useState } from 'react';
import { ItemTalentLevel, ItemTalentLevelProps, SingleLineText } from '@bloomays-lib/ui.shared';
import { getSkillsSorted } from '@bloomays-lib/utils.shared';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';

export type ItemTalentLevelListProps = {
  items?: ItemTalentLevelProps[];
  reduced?: boolean;
  forceShowAll?: boolean;
};

const ItemTalentLevelList = ({ items = [], reduced = false, forceShowAll = false }: ItemTalentLevelListProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['random']);
  const [isExpanded, setIsExpanded] = useState(false);

  const isCollapsible = isMobile;
  const isCollapsed = isCollapsible && !isExpanded;

  if (forceShowAll) {
    return (
      <StyledFilterList>
        {getSkillsSorted(items).map((item, i) => {
          return <ItemTalentLevel {...item} key={`${i}`} />;
        })}
      </StyledFilterList>
    );
  }

  if (reduced && items.length > 5) {
    return (
      <StyledFilterList>
        {getSkillsSorted(items)
          .slice(0, 5)
          .map((item, i) => {
            return <ItemTalentLevel {...item} key={`${i}`} />;
          })}
        <SingleLineText text={`+ ${items.length - 5} autres`} />
      </StyledFilterList>
    );
  }

  if (isCollapsible && items.length <= 25) {
    return (
      <StyledFilterList>
        {getSkillsSorted(items).map((item, i) => {
          return <ItemTalentLevel {...item} key={`${i}`} />;
        })}
      </StyledFilterList>
    );
  }

  return (
    <>
      <CollapsibleContainer $isCollapsed={isCollapsed}>
        <StyledFilterList>
          {getSkillsSorted(items).map((item, i) => {
            return <ItemTalentLevel {...item} key={`${i}`} />;
          })}
        </StyledFilterList>
      </CollapsibleContainer>
      {isCollapsible && items.length > 0 && (
        <ToggleButton
          type="button"
          onClick={() => {
            setIsExpanded((prev) => !prev);
          }}
        >
          <SingleLineText text={isExpanded ? t('seeLess') : t('seeMore')} />
        </ToggleButton>
      )}
    </>
  );
};

export default ItemTalentLevelList;
export { ItemTalentLevelList };

const StyledFilterList = styled('div')(
  () => `
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 8px;
`,
);

const CollapsibleContainer = styled('div')<{ $isCollapsed: boolean }>(({ $isCollapsed }) => ({
  position: 'relative',
  maxHeight: $isCollapsed ? '90px' : 'none',
  overflow: $isCollapsed ? 'hidden' : 'visible',
}));

const ToggleButton = styled('button')(({ theme }) => ({
  marginTop: theme.spacing(1),
  background: theme.palette.neutral[20],
  border: 'none',
  borderRadius: '24px',
  padding: 0,
  cursor: 'pointer',
  height: '36px',

  '& p': {
    fontSize: '13px',
  },
}));
